import { ClubManagementData } from '../../hooks/useClubManagementData'
import { RoleBadge } from '../ui'

interface ClubListItemProps {
  club: ClubManagementData
  isSelected: boolean
  onClick: () => void
  compact?: boolean
}

const ClubListItem = ({ club, isSelected, onClick, compact = false }: ClubListItemProps) => {
  if (compact) {
    return (
      <button
        onClick={onClick}
        className={`w-full text-left px-3 py-2.5 border-l-2 transition-colors ${
          isSelected
            ? 'border-l-primary-600 bg-primary-50'
            : 'border-l-transparent hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">{club.clubName}</div>
            <div className="text-xs text-gray-500 truncate">{club.shortAddress}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-400">{club.clubMemberCount}</span>
            <RoleBadge role={club.role.role} variant="dot" />
          </div>
        </div>
      </button>
    )
  }

  return (
    <div
      onClick={onClick}
      className={`p-3 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 ${
        isSelected ? 'bg-primary-50 border-l-2 border-l-primary-600' : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-gray-900 font-medium text-sm truncate">{club.clubName}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 flex-shrink-0">
          <span>{club.clubMemberCount} member{club.clubMemberCount !== 1 ? 's' : ''}</span>
          <RoleBadge role={club.role.role} variant="compact" />
        </div>
      </div>
      <div className="text-xs text-gray-400 mt-0.5 truncate">{club.shortAddress}</div>
    </div>
  )
}

interface SearchSortBarProps {
  searchTerm: string
  onSearchChange: (term: string) => void
  sortValue: string
  onSortChange: (value: string) => void
  compact?: boolean
}

export const SearchSortBar = ({ searchTerm, onSearchChange, sortValue, onSortChange, compact = false }: SearchSortBarProps) => (
  <div className={`flex gap-3 items-start ${compact ? 'flex-col' : 'flex-col sm:flex-row sm:items-center'} mb-4`}>
    <div className="relative flex-1 w-full">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        placeholder={compact ? 'Search...' : 'Search clubs by name or location...'}
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="w-full pl-10 pr-4 py-2 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
      />
    </div>
    <select
      value={sortValue}
      onChange={(e) => onSortChange(e.target.value)}
      className="px-3 py-2 border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
    >
      <option value="name-asc">Name A-Z</option>
      <option value="name-desc">Name Z-A</option>
      <option value="members-desc">Most Members</option>
      <option value="members-asc">Fewest Members</option>
      <option value="pending-desc">Most Pending</option>
      <option value="restrictions-desc">Restricted First</option>
      <option value="created-desc">Newest First</option>
    </select>
  </div>
)

interface ClubListPanelProps {
  clubs: ClubManagementData[]
  selectedClubId: number | null
  onSelectClub: (clubId: number) => void
  searchTerm: string
  onSearchChange: (term: string) => void
  sortValue: string
  onSortChange: (value: string) => void
  compact?: boolean
  className?: string
  emptySearchTerm?: string
}

export const ClubListPanel = ({
  clubs,
  selectedClubId,
  onSelectClub,
  searchTerm,
  onSearchChange,
  sortValue,
  onSortChange,
  compact = false,
  className = '',
  emptySearchTerm,
}: ClubListPanelProps) => {
  return (
    <div className={className}>
      <SearchSortBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        sortValue={sortValue}
        onSortChange={onSortChange}
        compact={compact}
      />

      <div className="border border-gray-200 overflow-hidden">
        {clubs.map((club) => (
          <ClubListItem
            key={club.clubId}
            club={club}
            isSelected={selectedClubId === club.clubId}
            onClick={() => onSelectClub(club.clubId)}
            compact={compact}
          />
        ))}
      </div>

      {clubs.length === 0 && emptySearchTerm && (
        <div className="text-center py-8">
          <p className="text-gray-500 text-sm mb-2">No clubs found matching &ldquo;{emptySearchTerm}&rdquo;</p>
          <button onClick={() => onSearchChange('')} className="text-primary-600 hover:text-primary-500 text-sm">
            Clear search
          </button>
        </div>
      )}
    </div>
  )
}

export { ClubListItem }
